import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, photoUrl, brochureUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthGate } from "@/contexts/AuthGateContext";
import { Button } from "@/components/ui/button";
import MapView from "@/components/MapView";
import {
  Calendar, MapPin, Building2, ShieldCheck, Download, Mail, Phone,
  MessageCircle, Loader2, CheckCircle2, Bed,
} from "lucide-react";

const FALLBACK =
  "https://images.unsplash.com/photo-1523217582562-09d0def993a6?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, formatPrice } = useLocale();
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${id}`)
      .then(({ data }) => setProject(data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-16 text-center text-muted-foreground">{t("common.loading")}</div>;
  if (!project) return <div className="p-16 text-center">Proyecto no encontrado</div>;

  const photos = project.photos?.length ? project.photos.map(photoUrl) : [FALLBACK];
  const monthLabel = t("projects.months")[project.delivery_month - 1];

  const brochureLink = project.brochure_path ? brochureUrl(project.brochure_path) : null;

  const whatsappUrl = project.agent?.phone
    ? `https://wa.me/${project.agent.phone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        `Hola, estoy interesado en el proyecto "${project.name}" (ID: ${project.project_id}). ¿Podemos hablar?`
      )}`
    : "";

  const contactWhatsapp = () => requireAuth(
    { type: "external_url", url: whatsappUrl },
    { title: t("gate.title_whatsapp"), sub: t("gate.sub_whatsapp") }
  );
  const revealPhone = () => requireAuth(
    { type: "reveal_phone" },
    { title: t("gate.title_phone"), sub: t("gate.sub_phone") }
  );
  const startChat = () => {
    setChatLoading(true);
    requireAuth(
      { type: "start_chat", property_id: project.project_id },
      { title: t("gate.title_chat"), sub: t("gate.sub_chat") }
    );
    setTimeout(() => setChatLoading(false), 800);
  };

  const unitLabel = (u) => {
    if (u.bedrooms === 0) return t("projects.unit_studio");
    const key = u.bedrooms === 1 ? "projects.unit_bedrooms" : "projects.unit_bedrooms_plural";
    return t(key).replace("{n}", u.bedrooms);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-10 pt-6 md:pt-14 pb-16">
      <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-projects">
        ← {t("nav.projects")}
      </Link>

      <div className="grid lg:grid-cols-[1fr,380px] gap-10 lg:gap-16 mt-6">
        <div>
          {/* Gallery */}
          <div className="aspect-[16/10] overflow-hidden bg-muted rounded-sm relative">
            <img src={photos[activePhoto]} alt={project.name} className="h-full w-full object-cover" data-testid="project-photo-main" />
            {project.confotur && (
              <div
                className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold shadow-lg"
                data-testid="confotur-badge"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> {t("projects.confotur")}
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {photos.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className={`aspect-square overflow-hidden rounded-sm border ${i === activePhoto ? "border-primary" : "border-border opacity-70 hover:opacity-100"}`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10">
            <div className="inline-flex items-center gap-1.5 label-eyebrow text-secondary">
              <Building2 className="h-3.5 w-3.5" /> {project.developer}
            </div>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-[1.05]">{project.name}</h1>
            <div className="mt-3 flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-4 w-4" /> {project.address}
            </div>
          </div>

          {/* KPI row */}
          <div className="mt-8 grid grid-cols-2 gap-6 border-y border-border py-6">
            <div>
              <div className="label-eyebrow">{t("projects.delivery")}</div>
              <div className="mt-1 font-serif text-2xl inline-flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {monthLabel} {project.delivery_year}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">{t("projects.available_units")}</div>
              <div className="mt-1 font-serif text-2xl inline-flex items-center gap-2">
                <Bed className="h-5 w-5 text-primary" />
                {project.units?.length || 0} tipologías
              </div>
            </div>
          </div>

          {project.description && (
            <div className="mt-10">
              <div className="label-eyebrow">{t("projects.description")}</div>
              <p className="mt-3 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{project.description}</p>
            </div>
          )}

          {/* Payment plan */}
          {project.payment_plan?.length > 0 && (
            <div className="mt-10">
              <div className="label-eyebrow mb-4">{t("projects.payment_plan")}</div>
              <div className="border border-border rounded-lg overflow-hidden" data-testid="payment-plan">
                {project.payment_plan.map((s, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-5 py-4 ${i < project.payment_plan.length - 1 ? "border-b border-border" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                        {i + 1}
                      </div>
                      <div className="font-medium">{s.label}</div>
                    </div>
                    <div className="font-serif text-2xl text-primary">{s.percent}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Units */}
          {project.units?.length > 0 && (
            <div className="mt-10">
              <div className="label-eyebrow mb-4">{t("projects.available_units")}</div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="units-list">
                {project.units.map((u, i) => (
                  <div key={i} className="border border-border rounded-lg p-5 hover:bg-accent/20 transition-colors">
                    <div className="flex items-center gap-2">
                      <Bed className="h-4 w-4 text-primary" />
                      <span className="font-serif text-xl">{unitLabel(u)}</span>
                    </div>
                    {u.area_min && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Desde {u.area_min} m²{u.area_max ? ` – ${u.area_max} m²` : ""}
                      </div>
                    )}
                    {u.price_from && (
                      <div className="font-serif text-lg text-primary mt-2">
                        {t("projects.from")} {formatPrice(u.price_from, project.currency)}
                      </div>
                    )}
                    {u.description && <div className="text-xs text-muted-foreground mt-2">{u.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confotur explainer */}
          {project.confotur && (
            <div className="mt-10 flex items-start gap-4 p-5 rounded-lg surface-soft border border-border">
              <ShieldCheck className="h-6 w-6 text-secondary shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">{t("projects.confotur")}</div>
                <p className="text-sm text-muted-foreground mt-1">{t("projects.confotur_desc")}</p>
              </div>
            </div>
          )}

          {/* Location map */}
          {typeof project.latitude === "number" && typeof project.longitude === "number" && (
            <div className="mt-10">
              <div className="label-eyebrow mb-3">{t("projects.location")}</div>
              <MapView
                properties={[{
                  property_id: project.project_id,
                  title: project.name,
                  address: project.address,
                  latitude: project.latitude,
                  longitude: project.longitude,
                  price: project.price_min,
                  currency: project.currency,
                  property_type: "villa",
                  bedrooms: 0,
                  square_meters: 0,
                  photos: project.photos || [],
                }]}
                height="380px"
              />
            </div>
          )}
        </div>

        {/* Sticky right rail */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4 border border-border p-6 md:p-7 rounded-lg bg-card">
          <div>
            <div className="label-eyebrow">{t("projects.price_range")}</div>
            <div className="mt-2 font-serif text-3xl text-primary leading-tight">
              {formatPrice(project.price_min, project.currency)}
            </div>
            <div className="text-muted-foreground text-sm">
              {t("projects.to")} {formatPrice(project.price_max, project.currency)}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="label-eyebrow mb-2">{t("projects.developer")}</div>
            <div className="font-medium">{project.developer}</div>
            {project.agent && (
              <div className="text-xs text-muted-foreground mt-1">
                {project.agent.name}
              </div>
            )}
          </div>

          {/* Brochure */}
          <div className="border-t border-border pt-4">
            <div className="label-eyebrow mb-2">{t("projects.brochure")}</div>
            {brochureLink ? (
              <Button
                asChild
                variant="outline"
                className="w-full rounded-full"
                data-testid="brochure-download-btn"
              >
                <a href={brochureLink} target="_blank" rel="noopener noreferrer" download>
                  <Download className="h-4 w-4 mr-2" />
                  {t("projects.brochure_download")}
                </a>
              </Button>
            ) : (
              <div className="text-xs text-muted-foreground">{t("projects.no_brochure")}</div>
            )}
          </div>

          {/* Contact buttons */}
          <div className="border-t border-border pt-4 space-y-2">
            {project.agent?.phone && (
              <Button
                type="button"
                onClick={contactWhatsapp}
                className="w-full rounded-full btn-hover-lift bg-[#25D366] hover:bg-[#1FBA57] text-white"
                data-testid="project-whatsapp-btn"
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.891c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c6.585 0 11.946-5.335 11.949-11.896C24 8.706 22.759 5.652 20.52 3.449z"/></svg>
                WhatsApp
              </Button>
            )}
            {project.agent?.phone && (
              user ? (
                <Button asChild variant="outline" className="w-full rounded-full" data-testid="project-phone-btn">
                  <a href={`tel:${project.agent.phone.replace(/[^\d+]/g, "")}`}>
                    <Phone className="h-4 w-4 mr-2" /> {project.agent.phone}
                  </a>
                </Button>
              ) : (
                <Button type="button" onClick={revealPhone} variant="outline" className="w-full rounded-full" data-testid="project-view-phone-btn">
                  <Phone className="h-4 w-4 mr-2" /> {t("property.view_phone")}
                </Button>
              )
            )}
            {(!user || user.user_id !== project.agent?.user_id) && (
              <Button
                type="button"
                onClick={startChat}
                variant="outline"
                className="w-full rounded-full"
                data-testid="project-chat-btn"
                disabled={chatLoading}
              >
                {chatLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
                {t("property.contact_chat")}
              </Button>
            )}
            {project.agent?.email && (
              <Button
                type="button"
                onClick={() => requireAuth(
                  { type: "external_url", url: `mailto:${project.agent.email}?subject=${encodeURIComponent(project.name + " · ID " + project.project_id)}` },
                  { title: t("gate.title_email"), sub: t("gate.sub_email") }
                )}
                variant="ghost"
                className="w-full rounded-full"
                data-testid="project-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> {t("property.contact")}
              </Button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
